import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Download, Phone, Mail, MapPin, Globe, FileText, Send } from "lucide-react";
import { useScrollReveal, useScrollToSuccess } from "../lib/hooks";
import { SectionBadge } from "../components/Shared";
import { PHONE, PHONE_LINK, EMAIL, FONT_HEAD, FONT_BODY, NAVY, GOLD } from "../lib/constants";

const PDF_URL = "/Cindyrella_Capability_Statement.pdf";

const COMPETENCIES = [
  "Janitorial & custodial operations",
  "Post-construction final cleaning",
  "After-hours & weekend service",
  "Multi-site service coordination",
  "High-turnover facility programs",
  "Restroom, lobby & common-area care",
];

const WHY_ITEMS = [
  { lead: "Hospitality-grade standards.", text: "5.0 client rating and detail-oriented service experience across luxury residences, vacation rentals, restaurants, and offices in the Naples area." },
  { lead: "Documented quality control.", text: "Service checklists, photographic documentation of completed work, and systematic follow-up on every engagement." },
  { lead: "Responsive scheduling.", text: "After-hours, weekend, and emergency coverage without disrupting your operations." },
  { lead: "Trusted access.", text: "Vetted personnel and established procedures for controlled and client-facing environments." },
];

const STATS = [
  { value: "2020", label: "Established" },
  { value: "5.0", label: "Client Rating" },
  { value: "24H", label: "Quote Response" },
  { value: "100%", label: "Licensed & Insured" },
];

const COMPANY_DATA = [
  { label: "Legal Name", value: "Cindyrella Cleaning Services, LLC" },
  { label: "Entity Type", value: "Florida Limited Liability Co." },
  { label: "UEI (SAM.gov)", value: "X5NKY2NNWZC1" },
  { label: "CAGE Code", value: "Pending" },
  { label: "Primary NAICS", value: "561720 — Janitorial Services" },
  { label: "Address", value: "3025 Driftwood Way, Unit 3206, Naples, FL 34109" },
];

const NAICS_CODES = [
  { code: "561720", desc: "Janitorial Services (Primary)" },
  { code: "561790", desc: "Other Services to Buildings" },
  { code: "238990", desc: "Specialty Trade Contractors" },
  { code: "561730", desc: "Landscaping Services" },
];

const PSC_CODES = [
  { code: "S201", desc: "Custodial/Janitorial Services" },
  { code: "S202", desc: "Facilities Operations Support" },
  { code: "Z1AA", desc: "Building Maintenance" },
  { code: "Z2AB", desc: "Facilities Mgmt. Support" },
];

const MARKETS = ["Federal", "State", "Local", "Commercial"];

const DownloadButton = ({ testid }) => (
  <a
    href={PDF_URL}
    download="Cindyrella_Capability_Statement.pdf"
    data-testid={testid}
    className="inline-flex items-center gap-2 px-7 py-4 text-base font-bold transition-all duration-300 hover:opacity-90 hover:shadow-lg"
    style={{ background: GOLD, color: NAVY, fontFamily: FONT_BODY }}
  >
    <Download size={18} />
    Download Capability Statement (PDF)
  </a>
);

const GovHero = () => (
  <section data-testid="gov-hero" className="pt-16 md:pt-20" style={{ background: NAVY }}>
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24 text-center">
      <SectionBadge dark>Government &amp; Commercial Contractor</SectionBadge>
      <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight" style={{ fontFamily: FONT_HEAD, letterSpacing: "-0.02em" }} data-testid="gov-hero-title">
        Capability Statement
      </h1>
      <p className="mt-5 text-base md:text-lg max-w-2xl mx-auto leading-relaxed" style={{ color: "rgba(255,255,255,0.7)", fontFamily: FONT_BODY }}>
        Cindyrella Cleaning Services, LLC
      </p>
      <div className="mt-8">
        <DownloadButton testid="gov-download-pdf-top" />
      </div>
    </div>
  </section>
);

const OverviewSection = () => {
  const ref = useScrollReveal();
  return (
    <section ref={ref} data-testid="gov-overview" className="py-16 md:py-24 section-hidden bg-white">
      <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
        <SectionBadge>Company Overview</SectionBadge>
        <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ fontFamily: FONT_HEAD, color: NAVY }}>
          Who We Are
        </h2>
        <p className="text-base leading-relaxed mb-6" style={{ color: "#4B5563", fontFamily: FONT_BODY }}>
          Cindyrella Cleaning Services, LLC is a licensed and insured facility services company
          based in Naples, Florida, established in 2020. We provide janitorial, custodial, and
          specialized cleaning for residential, commercial, hospitality, and government
          facilities — detail-oriented maintenance that protects our clients' reputation and
          operational continuity.
        </p>
        <p className="text-base italic" style={{ color: NAVY, fontFamily: FONT_BODY }}>
          "Leave every space better than we found it, every single time."
        </p>
      </div>
    </section>
  );
};

const StatRow = () => (
  <section data-testid="gov-stats" className="py-14" style={{ background: "#F9FAFB" }}>
    <div className="max-w-7xl mx-auto px-4 md:px-8">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
        {STATS.map(({ value, label }) => (
          <div key={label} data-testid={`gov-stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="text-4xl md:text-5xl font-bold mb-2" style={{ fontFamily: FONT_HEAD, color: NAVY }}>
              {value}
            </div>
            <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: GOLD, fontFamily: FONT_BODY }}>
              {label}
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const CompetenciesSection = () => {
  const ref = useScrollReveal();
  return (
    <section ref={ref} data-testid="gov-competencies" className="py-16 md:py-24 section-hidden bg-white">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <SectionBadge>Core Competencies</SectionBadge>
          <h2 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: FONT_HEAD, color: NAVY }}>
            What We Deliver
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-5">
          {COMPETENCIES.map((item) => (
            <div key={item} className="flex items-start gap-3">
              <CheckCircle size={18} style={{ color: GOLD, flexShrink: 0, marginTop: "3px" }} />
              {item === "Post-construction final cleaning" ? (
                <Link to="/services/post-construction-cleaning-naples-fl" data-testid="gov-post-construction-link" className="text-base underline-offset-4 hover:underline" style={{ color: "#2D2D2D", fontFamily: FONT_BODY }}>
                  Post-Construction Final Cleaning
                </Link>
              ) : (
                <span className="text-base" style={{ color: "#2D2D2D", fontFamily: FONT_BODY }}>{item}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const WhySection = () => {
  const ref = useScrollReveal();
  return (
    <section ref={ref} data-testid="gov-why" className="py-16 md:py-24 section-hidden" style={{ background: "#F9FAFB" }}>
      <div className="max-w-5xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <SectionBadge>Why Cindyrella</SectionBadge>
          <h2 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: FONT_HEAD, color: NAVY }}>
            The Cindyrella Difference
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {WHY_ITEMS.map(({ lead, text }) => (
            <div key={lead} className="bg-white p-7" style={{ border: "1px solid rgba(27,58,107,0.08)", borderLeft: `4px solid ${GOLD}` }}>
              <p className="text-base leading-relaxed" style={{ color: "#4B5563", fontFamily: FONT_BODY }}>
                <strong style={{ color: NAVY }}>{lead}</strong> {text}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const PastPerformanceSection = () => (
  <section data-testid="gov-past-performance" className="py-16 md:py-20" style={{ background: NAVY }}>
    <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
      <SectionBadge dark>Past Performance</SectionBadge>
      <p className="text-base md:text-lg leading-relaxed" style={{ color: "rgba(255,255,255,0.85)", fontFamily: FONT_BODY }}>
        Proven commercial performance with demanding hospitality, luxury residential, and
        commercial clients in Naples and Pelican Marsh, with quality systems built to
        hospitality standards. Commercial references available upon request.
      </p>
    </div>
  </section>
);

const DataRow = ({ label, value }) => (
  <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4 py-3" style={{ borderBottom: "1px solid rgba(27,58,107,0.1)" }}>
    <span className="text-xs font-bold uppercase tracking-widest sm:w-44 flex-shrink-0" style={{ color: GOLD, fontFamily: FONT_HEAD }}>{label}</span>
    <span className="text-sm font-semibold" style={{ color: "#2D2D2D", fontFamily: FONT_BODY }}>{value}</span>
  </div>
);

const CompanyDataBox = () => (
  <div className="bg-white p-7 md:p-9" style={{ border: "1px solid rgba(27,58,107,0.12)", borderTop: `4px solid ${GOLD}` }} data-testid="gov-company-data">
    <h3 className="text-xl font-bold mb-5 flex items-center gap-2" style={{ fontFamily: FONT_HEAD, color: NAVY }}>
      <FileText size={20} style={{ color: GOLD }} />
      Company Data
    </h3>
    {COMPANY_DATA.map((row) => <DataRow key={row.label} {...row} />)}
    <div className="pt-4 space-y-2.5">
      <a href={PHONE_LINK} data-testid="gov-phone-link" className="flex items-center gap-3 text-sm font-semibold transition-colors hover:text-[#b8922a]" style={{ color: NAVY, fontFamily: FONT_BODY }}>
        <Phone size={15} style={{ color: GOLD }} /> {PHONE}
      </a>
      <a href={`mailto:${EMAIL}`} data-testid="gov-email-link" className="flex items-center gap-3 text-sm font-semibold transition-colors hover:text-[#b8922a]" style={{ color: NAVY, fontFamily: FONT_BODY }}>
        <Mail size={15} style={{ color: GOLD }} /> {EMAIL}
      </a>
      <div className="flex items-center gap-3 text-sm font-semibold" style={{ color: NAVY, fontFamily: FONT_BODY }}>
        <Globe size={15} style={{ color: GOLD }} /> cindyrellacleaningllc.com
      </div>
    </div>
  </div>
);

const CodeTable = ({ title, rows, testid }) => (
  <div className="bg-white p-7 md:p-9" style={{ border: "1px solid rgba(27,58,107,0.12)", borderTop: `4px solid ${GOLD}` }} data-testid={testid}>
    <h3 className="text-xl font-bold mb-5" style={{ fontFamily: FONT_HEAD, color: NAVY }}>{title}</h3>
    <div className="space-y-3">
      {rows.map(({ code, desc }) => (
        <div key={code} className="flex items-baseline gap-4">
          <span className="text-sm font-bold w-16 flex-shrink-0" style={{ color: GOLD, fontFamily: FONT_HEAD }}>{code}</span>
          <span className="text-sm" style={{ color: "#4B5563", fontFamily: FONT_BODY }}>{desc}</span>
        </div>
      ))}
    </div>
  </div>
);

const DataSection = () => {
  const ref = useScrollReveal();
  return (
    <section ref={ref} data-testid="gov-data-section" className="py-16 md:py-24 section-hidden" style={{ background: "#F9FAFB" }}>
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <CompanyDataBox />
          <div className="space-y-8">
            <CodeTable title="NAICS Codes" rows={NAICS_CODES} testid="gov-naics" />
            <CodeTable title="PSC Codes" rows={PSC_CODES} testid="gov-psc" />
          </div>
        </div>
        <div className="text-center" data-testid="gov-markets">
          <h3 className="text-sm font-bold uppercase tracking-widest mb-5" style={{ color: NAVY, fontFamily: FONT_HEAD }}>Markets Served</h3>
          <div className="flex flex-wrap justify-center gap-3">
            {MARKETS.map((m) => (
              <span key={m} className="px-6 py-2.5 text-sm font-bold" style={{ background: "rgba(212,168,67,0.14)", color: "#b8922a", fontFamily: FONT_HEAD, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                {m}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const GOV_INPUT_STYLE = {
  fontFamily: FONT_BODY,
  border: "1px solid rgba(27,58,107,0.22)",
  borderRadius: "2px",
  fontSize: "14px",
  color: "#2D2D2D",
  background: "#FFFFFF",
};

const GovField = ({ label, name, type = "text", required = false, placeholder }) => (
  <div>
    <label className="block text-xs font-bold mb-1.5 uppercase tracking-wide" style={{ color: NAVY, fontFamily: FONT_BODY }}>
      {label}{required && " *"}
    </label>
    <input name={name} type={type} required={required} placeholder={placeholder} data-testid={`gov-form-${name}`} className="w-full px-3 py-3" style={GOV_INPUT_STYLE} />
  </div>
);

const GovFormSuccess = ({ name }) => (
  <div className="text-center py-10" data-testid="gov-form-success">
    <CheckCircle size={44} className="mx-auto mb-4" style={{ color: GOLD }} />
    <h3 className="text-xl font-bold mb-2" style={{ fontFamily: FONT_HEAD, color: NAVY }}>
      Thank you{name ? `, ${name}` : ""}!
    </h3>
    <p className="text-sm" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>
      Your reference request has been received. We'll respond within 24 hours.
    </p>
  </div>
);

const readReferencesPayload = (form) => {
  const fd = new FormData(form);
  const get = (k) => (fd.get(k) || "").toString().trim();
  const org = get("organization");
  const note = get("message");
  return {
    fullName: get("fullName"),
    phone: get("phone"),
    email: get("email"),
    service: "Government — Reference Request",
    message: `Organization/Agency: ${org || "Not specified"}${note ? `\n\n${note}` : ""}`,
  };
};

const useReferencesForm = () => {
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submittedName, setSubmittedName] = useState(null);
  useEffect(() => setReady(true), [setReady]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = readReferencesPayload(e.target);
    setLoading(true);
    setError("");
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/quote`, payload);
      setSubmittedName(payload.fullName.split(" ")[0]);
    } catch (err) {
      setError(
        err.response?.status === 429
          ? "Too many requests — please try again in a few minutes or call us directly."
          : "Something went wrong. Please call (239) 244-1934 or email info@cindyrellaclean.com."
      );
    } finally {
      setLoading(false);
    }
  };

  return { ready, loading, error, submittedName, handleSubmit };
};

const ReferencesForm = () => {
  const { ready, loading, error, submittedName, handleSubmit } = useReferencesForm();
  useScrollToSuccess(submittedName !== null, '[data-testid="gov-form-success"]');

  if (submittedName !== null) return <GovFormSuccess name={submittedName} />;
  return (
    <form onSubmit={handleSubmit} className="space-y-4 text-left" data-testid="gov-references-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <GovField label="Full Name" name="fullName" required placeholder="Jane Smith" />
        <GovField label="Organization / Agency" name="organization" placeholder="Collier County Procurement" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <GovField label="Email" name="email" type="email" required placeholder="you@agency.gov" />
        <GovField label="Phone" name="phone" type="tel" required placeholder="(239) 555-0100" />
      </div>
      <div>
        <label className="block text-xs font-bold mb-1.5 uppercase tracking-wide" style={{ color: NAVY, fontFamily: FONT_BODY }}>
          Message
        </label>
        <textarea name="message" rows={3} placeholder="Tell us about your solicitation or the references you need…" data-testid="gov-form-message" className="w-full px-3 py-3 resize-none" style={GOV_INPUT_STYLE} />
      </div>
      {error && (
        <p data-testid="gov-form-error" className="px-3 py-2 text-xs" style={{ background: "rgba(239,68,68,0.07)", color: "#DC2626", fontFamily: FONT_BODY }}>
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={loading || !ready}
        data-testid="gov-form-submit-btn"
        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-bold transition-all duration-300 hover:opacity-90 disabled:opacity-60"
        style={{ background: NAVY, color: "#FFFFFF", fontFamily: FONT_BODY }}
      >
        <Send size={16} />
        {loading ? "Sending…" : "Request References"}
      </button>
    </form>
  );
};

const ReferencesSection = () => {
  const ref = useScrollReveal();
  return (
    <section ref={ref} data-testid="gov-references-section" className="py-16 md:py-24 section-hidden bg-white">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <SectionBadge>Request References</SectionBadge>
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: FONT_HEAD, color: NAVY }}>
            Need Commercial References?
          </h2>
          <p className="text-base max-w-xl mx-auto" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>
            Procurement officers and contracting specialists: request our commercial
            references or a confidential facility walkthrough. We respond within 24 hours.
          </p>
        </div>
        <div className="p-7 md:p-9" style={{ background: "#F9FAFB", border: "1px solid rgba(27,58,107,0.12)", borderTop: `4px solid ${GOLD}` }}>
          <ReferencesForm />
        </div>
      </div>
    </section>
  );
};

const GovCTA = () => (
  <section data-testid="gov-cta" className="py-16 md:py-20" style={{ background: NAVY }}>
    <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
      <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: FONT_HEAD }}>
        Ready to <span style={{ color: GOLD }}>Work With Us</span>?
      </h2>
      <p className="text-base mb-8" style={{ color: "rgba(255,255,255,0.7)", fontFamily: FONT_BODY }}>
        Download our full capability statement or contact us directly.
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <DownloadButton testid="gov-download-pdf-bottom" />
        <a
          href={PHONE_LINK}
          data-testid="gov-cta-call-btn"
          className="flex items-center justify-center gap-2 px-8 py-4 text-base font-bold border-2 text-white transition-all duration-300 hover:bg-white hover:text-[#1B3A6B]"
          style={{ borderColor: "rgba(255,255,255,0.5)", fontFamily: FONT_BODY }}
        >
          <Phone size={18} />
          {PHONE}
        </a>
      </div>
    </div>
  </section>
);

export default function GovernmentContractsPage() {
  return (
    <div data-testid="government-contracts-page">
      <GovHero />
      <OverviewSection />
      <StatRow />
      <CompetenciesSection />
      <WhySection />
      <PastPerformanceSection />
      <DataSection />
      <ReferencesSection />
      <GovCTA />
    </div>
  );
}
